import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import WhatsAppLogo from "@/components/site/WhatsAppLogo";
import BusinessHours from "@/components/site/BusinessHours";
import { useI18n } from "@/i18n/I18nProvider";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS = ["Steel Gates", "Railings", "Stair Railings", "Safety Doors", "Steel Windows", "Steel Swings", "Steel Furniture", "Custom Fabrication", "Other"];

export default function ContactPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", email: "", product: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim() || form.message.length < 8) e.message = "Please share a little more";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.contact.success);
      setForm({ name: "", phone: "", email: "", product: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-36 pb-20 bg-white">
      <Toaster position="top-center" toastOptions={{ style: { background: "#fff", border: "1px solid #E2E8F0", color: "#0F172A" } }} />
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-6">
            <div className="label-eyebrow mb-3">{t.contact.eyebrow}</div>
            <h1 className="font-display font-medium text-slate-900 text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">{t.contact.title}</h1>
            <p className="mt-6 md:mt-8 text-slate-600 text-lg leading-relaxed max-w-lg">{t.contact.sub}</p>
          </div>
          <div className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ContactCard icon={<MapPin size={18} />} label={t.contact.addressLabel} href="https://maps.google.com/?q=Ramdev+Steel+Lingsugur+584122">
              Ramdev Steel, Lingsugur,<br />Karnataka – 584122
            </ContactCard>
            <ContactCard icon={<Phone size={18} />} label={t.contact.phoneLabel} href="tel:+919731852956">+91 97318 52956</ContactCard>
            <ContactCard icon={<WhatsAppLogo size={18} />} label={t.contact.waLabel} href="https://wa.me/919731852956">Chat with us instantly</ContactCard>
            <ContactCard icon={<Mail size={18} />} label={t.contact.emailLabel} href="mailto:ramdevsteelfurniture369@gmail.com">
              <span className="break-all">ramdevsteelfurniture369@gmail.com</span>
            </ContactCard>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-12">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={onSubmit}
            data-testid={CONTACT.form}
            className="lg:col-span-7 p-6 md:p-10 border border-slate-200 rounded-xl bg-white shadow-sm"
          >
            <h2 className="font-display text-slate-900 text-2xl md:text-3xl tracking-tight mb-6">Send an enquiry</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t.contact.name} error={errors.name}>
                <input data-testid={CONTACT.name} value={form.name} onChange={(e) => update("name", e.target.value)} className="input" />
              </Field>
              <Field label={t.contact.phone} error={errors.phone}>
                <input data-testid={CONTACT.phone} value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input" inputMode="tel" />
              </Field>
              <Field label={t.contact.email} error={errors.email}>
                <input data-testid={CONTACT.email} value={form.email} onChange={(e) => update("email", e.target.value)} className="input" type="email" />
              </Field>
              <Field label={t.contact.product}>
                <select data-testid={CONTACT.product} value={form.product} onChange={(e) => update("product", e.target.value)} className="input appearance-none">
                  <option value="">—</option>
                  {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label={t.contact.message} error={errors.message}>
                <textarea data-testid={CONTACT.message} value={form.message} onChange={(e) => update("message", e.target.value)} rows={5} className="input resize-none" />
              </Field>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={loading}
                className="btn-steel inline-flex items-center gap-2 px-7 py-3.5 text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full disabled:opacity-60"
              >
                {loading ? t.contact.sending : t.contact.submit} <ArrowUpRight size={14} />
              </button>
              <a href="https://wa.me/919731852956" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#25D366] hover:bg-[#1EBE57] text-white text-xs font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full transition-colors">
                <WhatsAppLogo size={14} /> WhatsApp
              </a>
            </div>
          </motion.form>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <BusinessHours />
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm h-64">
              <iframe
                title="Ramdev Steel — Lingsugur"
                src="https://www.google.com/maps?q=Ramdev+Steel+Lingsugur+Karnataka+584122&output=embed"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* AdSense-ready slot */}
        <div className="ad-slot" id="ad-slot-contact" data-testid="ad-slot-contact">
          Sponsored space · AdSense-ready
        </div>
      </div>

      <style>{`
        .input {
          width: 100%; background: #fff; border: 1px solid #E2E8F0;
          color: #0F172A; padding: 13px 15px; border-radius: 8px;
          font-family: "Satoshi", sans-serif; font-size: 14.5px;
          transition: border-color 200ms, box-shadow 200ms;
        }
        .input:focus { outline: none; border-color: #1E3A5F; box-shadow: 0 0 0 3px rgba(30,58,95,0.12); }
        .input::placeholder { color: #94A3B8; }
      `}</style>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-xs text-red-600 font-grotesk">{error}</span>}
    </label>
  );
}

function ContactCard({ icon, label, href, children }) {
  const inner = (
    <div className="card-hover p-5 border border-slate-200 rounded-xl bg-white h-full">
      <div className="flex items-center gap-2.5 text-[var(--steel)] mb-3">{icon}<span className="label-eyebrow text-slate-500">{label}</span></div>
      <div className="text-slate-900 text-[14.5px] leading-snug font-medium">{children}</div>
    </div>
  );
  if (href) return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{inner}</a>;
  return inner;
}
